import React from "react";
import { useEffect, useState } from "react";
import { ethers } from "ethers";
import { contractAddresses } from '../../web3/contractsAddresses';
import PagerTokenCard from "../PagerTokenCard";

const utilities = require("../utilities");

const CypherPagerABI = [
    "function tokenURI(uint256 tokenId) view returns (string)",
    "function totalSupply() view returns (uint256)"
];

function CypherPagers() {

    const [tokens, setTokens] = useState([]);
    const [supply, setSupply] = useState(0);
    const [txError, setTxError] = useState(undefined);
    const [paginator, setPaginator] = useState(0);
    const previous = paginator - 1;
    const next = paginator + 1;
    const pageSize = 8;

    // Shared singleton provider — no per-render provider creation (src/web3/providers.js)
    const { infuraProvider } = require("../../web3/providers");

    const cypherPagerReadContract = new ethers.Contract(
        contractAddresses.CypherPager,
        CypherPagerABI,
        infuraProvider
    );

    useEffect(() => {
        getSupply();
    }, []);

    useEffect(() => {
        getPagers();
    }, [paginator]);

    const getSupply = async () => {
        try {
            const _supply = await cypherPagerReadContract.totalSupply();
            setSupply(Number(_supply));
        } catch (error) {
            console.log(error);
        }
    }

    const getPagers = async () => {
        let fetchedTokens = [];
        try {
            setTokens([]);
            for (let i = paginator * pageSize; i < (paginator * pageSize + pageSize); i++) {
                try {
                    let tokenURI = await cypherPagerReadContract.tokenURI(i);
                    const tokenURIDecoded = utilities.parseBase64DataURI(tokenURI);
                    const tokenURIJSONDecoded = JSON.parse(tokenURIDecoded);
                    const tokenEntry = {
                        tokenId: i.toString(),
                        name: tokenURIJSONDecoded.name,
                        traits: tokenURIJSONDecoded.attributes,
                        image: tokenURIJSONDecoded.image
                    }
                    fetchedTokens.push(tokenEntry);
                    setTokens([...fetchedTokens]);
                } catch (error) {
                    console.log(error);
                }
            }
        } catch (error) {
            console.log(error);
            setTxError(error);
        }
    }

    function loadPrevious() {
        if (paginator < 1) {
            return;
        }
        setPaginator(previous);
    }

    function loadNext() {
        if ((next * pageSize) >= supply) {
            return;
        }
        setPaginator(next);
    }

    const showScreen = (fn) => {
        const frame = document.getElementById('pagerMockup');
        if (frame === null) {
            return;
        }
        try {
            frame.contentWindow[fn]();
        } catch (error) {
            console.log(error);
        }
        frame.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return (
        <div className="ProjectInfo">
            <p className="highlightInfo">CYPHERPAGER</p>
            <iframe id="pagerMockup" title="Cypherpager mockup" src={`${process.env.PUBLIC_URL}/mockup/pager_mockup.html`} style={{ width: '100%', aspectRatio: '4 / 1', border: 'none', display: 'block', backgroundColor: '#000' }}></iframe>
            <div className="descriptionDiv">
                <p className="starter">The Cypherpager is an on-chain encrypted messaging device. Like every artefact, it is soulbound to its cypherdude : it lives in the token bound wallet, and follows the cypherdude wherever it goes. Messages are never sent in clear. A message is encrypted with the recipient's active epoch key, then the cipher is hidden inside ascii art — the art itself is the carrier — and inscribed on-chain to the recipient's pager. Before a pager can receive anything, its owner must INITIALIZE it : this opens epoch 0 and registers the first public key. Keys can be rotated at any time (for example after a transfer of the cypherdude) : FREEZE seals the active epoch so it can't receive new messages, then OPEN EPOCH registers a fresh key and receiving resumes. The device has three screens, selected with the three icons on the left :</p>
                <p className="highlightInfo" style={{ cursor: 'pointer' }} onClick={() => showScreen('openRead')}>READ</p>
                <p>The READ screen displays the latest message received, as its ascii art carrier. Click the display : the pager extracts the cipher fields from the art and runs the key check — the commitment and the authentication tag are verified against your active epoch key. If the message was encrypted for another key, the screen flashes KEY MISMATCH and the art keeps its secret. If the key matches, a scramble wave sweeps the grid from left to right and deciphers the message in place. Once deciphered, each click cycles the views : the decoded message centered on the animated grid, then the message info (sender address, block number, epoch, nonce, ephemeral public key, cipher commitment and the check results), then back to the original art.</p>
                <p className="highlightInfo" style={{ cursor: 'pointer' }} onClick={() => showScreen('openSend')}>SEND</p>
                <p>The SEND screen inscribes a new message. First pick the recipient : a cypherdude id from 0 to 2047, resolved to its pagerId — if the dude owns no pager, there is nothing to receive the message and sending is blocked. Then type your secret : 93 bytes maximum, encrypted before anything leaves your machine. The carrier art can be built three different ways. LOAD IMAGE : a png or jpeg is converted to ascii art, and the cipher bits are hidden inside its characters. CLEAR TEXT : up to 20 visible characters become huge scrolling ascii letters — a public banner anyone can read — while the encrypted secret hides inside the glyphs of the letters themselves. Or load nothing at all : the pager falls back to a randomart signature, seeded by your message and the timestamp, unique on every send. PREVIEW shows the exact art that will be inscribed. SEND encrypts the secret with the recipient's active epoch key, embeds the cipher in the art, produces a zero knowledge proof (Groth16) that the inscription is well formed, and inscribes everything to the recipient's pager with sendPrivate.</p>
                <p className="highlightInfo" style={{ cursor: 'pointer' }} onClick={() => showScreen('openBrowse')}>BROWSE</p>
                <p>The BROWSE screen lists every message received, epoch by epoch. The chevrons step through the key epochs — each header shows the epoch's public key, its opening block, and whether it is still active or frozen. Every row displays the sender, the block number, the cipher commitment and the nonce, with a NEW flag on unread messages : click a row to open it on the READ screen. This screen also carries the epoch lifecycle controls for the owner : INITIALIZE opens epoch 0 and registers your first key (one time only — the pager can receive from then on), FREEZE seals the active epoch as step one of a key rotation, and OPEN EPOCH registers a fresh key to start the next epoch, resuming reception.</p>
            </div>
            <div className="ProjectInfo">
                <p>COLLECTION</p>
                <div className="adminTools">
                    <div className="Paginators">
                        {paginator < 1 ?
                            '' :
                            <button onClick={loadPrevious} className="uxBtn">{`${(previous) * pageSize} to ${(paginator * pageSize) - 1}`}</button>
                        }
                        <p className="currentPagination">{`${(paginator) * pageSize} to ${Math.min((next * pageSize) - 1, Math.max(supply - 1, 0))}`}</p>
                        {(next * pageSize) >= supply ?
                            '' :
                            <button onClick={loadNext} className="uxBtn">{`${(next * pageSize)} to ${Math.min((next * pageSize) + (pageSize - 1), supply - 1)}`}</button>
                        }
                    </div>
                </div>
                <div className="pagerCollectionContainer">
                    {tokens.length < 1 ? `Fetching Cypherpagers collection` : tokens.map((token, id) => {
                        return <PagerTokenCard tokens={token} key={id} />
                    })}
                </div>
            </div>
        </div>
    )
}

export default CypherPagers;
